import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, CheckCircle, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import FormInput from './FormInput';
import { forgotPasswordSchema } from '../../schemas/authSchemas';
import { useAuthContext } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const ForgotPasswordModal = ({ isOpen, onClose, onBackToLogin }) => {
  const [emailSent, setEmailSent] = useState(false);
  const { forgotPassword, isLoading } = useAuthContext();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onChange'
  });

  const onSubmit = async (data) => {
    const result = await forgotPassword(data.email);
    
    if (result.success) {
      setEmailSent(true);
      toast.success('Email enviado correctamente', {
        icon: '✅',
        duration: 3000
      });
    } else {
      toast.error('Error al enviar el email', {
        icon: '❌',
        duration: 3000
      });
    }
  };

  const handleClose = () => {
    setEmailSent(false);
    onClose();
  };

  const handleBackToLogin = () => {
    setEmailSent(false);
    onBackToLogin();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <AnimatePresence mode="wait">
          {!emailSent ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl font-bold mb-2">¿Olvidaste tu contraseña?</h2>
                <p className="text-muted-foreground">
                  Ingresa tu email y te enviaremos un link para recuperarla
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <FormInput
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="tu@email.com"
                  icon={<Mail className="w-5 h-5" />}
                  error={errors.email?.message}
                  required
                  register={register}
                />

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={isLoading || !isValid}
                >
                  {isLoading ? 'Enviando...' : 'Enviar link de recuperación'}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleBackToLogin}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al login
                </Button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="text-center py-8"
            >
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-success" />
              </div>
              <h2 className="text-2xl font-bold mb-4">¡Email enviado!</h2>
              <p className="text-muted-foreground mb-2">
                Hemos enviado un link de recuperación a:
              </p>
              <p className="font-medium mb-6">{getValues('email')}</p>
              <p className="text-sm text-muted-foreground mb-8">
                Revisa tu bandeja de entrada y sigue las instrucciones para restablecer tu contraseña.
              </p>
              <Button
                onClick={handleBackToLogin}
                className="w-full"
                size="lg"
              >
                Volver al login
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;